import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Empty,
  Flex,
  Space,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { Download, RotateCw } from 'lucide-react'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  ActorTypeEnum,
  organizationsAuditLogsListOptions,
  type AuditLogList
} from 'src/api/main'
import {
  ActiveFilterTags,
  AuditActionTag,
  DateTimeCell,
  FilterSelect,
  MoreFilters,
  ProTableContainer,
  SearchInput,
  UserCell
} from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import { FilterLabelsProvider } from 'src/context'
import { AuditExportModal } from 'src/features/workspaces/components'
import {
  useActorFilter,
  useFacetFilter,
  useHasWorkspacePermissions,
  useQueryParams,
  useQueryUpdate
} from 'src/hooks'
import { paths } from 'src/router'
import {
  defaultFilters,
  getAuditEventCode,
  humanizeAuditCode,
  getActorlessLabel,
  toAuditAcceptLanguage,
  toDisplayString,
  translateAuditAction,
  translateAuditActorType,
  WorkspacePermissionCode
} from 'src/utils'
import type { ActiveFilterConfig } from 'src/utils'

const { Text } = Typography
const { RangePicker } = DatePicker

type Props = {
  applicationId: number
  organizationId: number
  /** Rendered as the left slot of the toolbar's top row (view switcher). */
  viewSelector: ReactNode
  /** Optional heading rendered in the top-left of the toolbar. */
  header?: ReactNode
}

const TABLE_SCROLL_MARGIN_TOP = 72

const EventsPanelBase = ({
  applicationId,
  organizationId,
  viewSelector,
  header
}: Props) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const tableTopRef = useRef<HTMLDivElement>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const canExportAudit = useHasWorkspacePermissions(
    WorkspacePermissionCode.AUDIT_EXPORT
  )
  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()

  const page = Number(params.page) || defaultFilters.page
  const perPage =
    Number(params.page_size ?? params.per_page) || defaultFilters.per_page
  const searchTerm = String(params.search ?? '')
  const entityTypeFilter = String(params.entity_type ?? '')
  const entityIdFilter = String(params.entity_id ?? '')
  const actionFilter = String(params.action ?? '')
  const actorTypeFilter = String(params.actor_type ?? '')
  const actorIdFilter = String(params.actor_id ?? '')
  const fromFilter = String(params.from ?? '')
  const toFilter = String(params.to ?? '')

  const language = i18n.resolvedLanguage ?? i18n.language

  const auditEnabled = organizationId > 0 && applicationId > 0
  const acceptLanguage = toAuditAcceptLanguage(language)

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    ...organizationsAuditLogsListOptions({
      path: { organization_id: organizationId },
      query: {
        application_id: applicationId || undefined,
        limit: perPage,
        offset: (page - 1) * perPage,
        search: searchTerm || undefined,
        entity_type: entityTypeFilter || undefined,
        entity_id: entityIdFilter || undefined,
        action: actionFilter || undefined,
        actor_type: actorTypeFilter
          ? (actorTypeFilter as ActorTypeEnum)
          : undefined,
        actor_id: actorIdFilter || undefined,
        timestamp_gte: fromFilter || undefined,
        timestamp_lte: toFilter || undefined
      },
      headers: { 'Accept-Language': acceptLanguage }
    }),
    enabled: auditEnabled,
    placeholderData: (previousData) => previousData,
    // Opt out of the app-wide refetchOnMount:false. New events land constantly, so
    // re-entering the page must hit the backend instead of rendering the old page.
    refetchOnMount: 'always',
    // Audit rows are append-only and land constantly: also re-fetch when the user
    // comes back to the browser tab, since a mount-only refetch misses the case
    // where the action was performed elsewhere and this page stayed open.
    refetchOnWindowFocus: true
  })

  const rows = data?.results ?? []
  const total = data?.count ?? 0
  const rangeValue = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    const fromDay = fromFilter ? dayjs(fromFilter) : null
    const toDay = toFilter ? dayjs(toFilter) : null

    return fromDay || toDay ? [fromDay, toDay] : null
  }, [fromFilter, toFilter])

  const actionLabel = useCallback(
    (value: string) => translateAuditAction(t, value),
    [t]
  )
  const action = useFacetFilter({
    organizationId,
    field: 'action',
    selected: actionFilter,
    getLabel: actionLabel,
    enabled: auditEnabled,
    applicationId: applicationId || undefined
  })
  const entityType = useFacetFilter({
    organizationId,
    field: 'entity_type',
    selected: entityTypeFilter,
    getLabel: humanizeAuditCode,
    enabled: auditEnabled,
    applicationId: applicationId || undefined
  })
  const actorTypeLabel = useCallback(
    (value: string) => translateAuditActorType(t, value),
    [t]
  )
  const actorType = useFacetFilter({
    organizationId,
    field: 'actor_type',
    selected: actorTypeFilter,
    getLabel: actorTypeLabel,
    enabled: auditEnabled,
    applicationId: applicationId || undefined
  })
  const actor = useActorFilter(organizationId, actorIdFilter, rows)

  const filtersDirty =
    searchTerm !== '' ||
    actionFilter !== '' ||
    entityTypeFilter !== '' ||
    fromFilter !== '' ||
    toFilter !== '' ||
    entityIdFilter !== '' ||
    actorTypeFilter !== '' ||
    actorIdFilter !== ''

  const filterTagItems: ActiveFilterConfig[] = [
    {
      type: 'select',
      key: 'action',
      label: t('workspace.apps.auditTab.filters.actionPlaceholder')
    },
    {
      type: 'select',
      key: 'actor_id',
      label: t('workspace.apps.auditTab.filters.actorIdPlaceholder')
    },
    {
      type: 'select',
      key: 'entity_type',
      label: t('workspace.apps.auditTab.filters.entityTypePlaceholder')
    },
    {
      type: 'select',
      key: 'entity_id',
      label: t('workspace.apps.auditTab.filters.entityIdPlaceholder')
    },
    {
      type: 'select',
      key: 'actor_type',
      label: t('workspace.apps.auditTab.filters.actorTypePlaceholder')
    },
    {
      type: 'dateRange',
      key: 'from',
      from: 'from',
      to: 'to',
      label: t('workspace.apps.auditTab.filters.dateRangeLabel'),
      clearKeys: ['from', 'to']
    }
  ]

  const moreFiltersActiveCount = [
    entityTypeFilter,
    entityIdFilter,
    actorTypeFilter,
    fromFilter || toFilter
  ].filter((value) => value !== '').length

  const setFilter = (patch: Record<string, unknown>) =>
    updateQuery({ ...params, page: defaultFilters.page, ...patch })

  const resetMoreFilters = () =>
    setFilter({
      entity_type: null,
      entity_id: null,
      actor_type: null,
      from: null,
      to: null
    })

  const handleRangeChange = (next: [Dayjs | null, Dayjs | null] | null) => {
    setFilter({
      from: next?.[0] ? next[0].startOf('day').toISOString() : null,
      to: next?.[1] ? next[1].endOf('day').toISOString() : null
    })
  }

  const clearFilters = () => {
    setFilter({
      search: null,
      entity_type: null,
      entity_id: null,
      action: null,
      actor_type: null,
      actor_id: null,
      from: null,
      to: null
    })
  }

  // The actor dropdown is built from the organization's member directory, so a
  // selected person can only ever be a `user`. Keeping both would silently return
  // nothing; drop the actor rather than leave an empty table with no reason shown.
  const handleActorTypeChange = (value: string) => {
    setFilter({
      actor_type: value || null,
      ...(value && value !== ActorTypeEnum.USER && actorIdFilter
        ? { actor_id: null }
        : {})
    })
  }

  const scrollToTableTop = useCallback(() => {
    requestAnimationFrame(() => {
      tableTopRef.current?.scrollIntoView({
        block: 'start',
        behavior: 'auto'
      })
    })
  }, [])

  const columns: ColumnsType<AuditLogList> = useMemo(
    () => [
      {
        title: t('workspace.apps.auditTab.columns.time'),
        dataIndex: 'timestamp',
        key: 'timestamp',
        fixed: 'left',
        width: 150,
        render: (value: string) => (
          <DateTimeCell value={value} variant="auto" style={{ fontSize: 12 }} />
        )
      },
      {
        title: t('workspace.apps.auditTab.columns.event'),
        dataIndex: 'event_type',
        key: 'event_type',
        width: 240,
        render: (_eventType: string | null, record) => (
          <Flex vertical gap={4}>
            <Text style={{ fontSize: 12 }}>
              {translateAuditAction(t, getAuditEventCode(record))}
            </Text>
            <Space size={4} wrap>
              <AuditActionTag action={record.action} maxWidth={110} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {record.entity_type} #{record.entity_id}
              </Text>
            </Space>
          </Flex>
        )
      },
      {
        title: t('workspace.apps.auditTab.columns.actor'),
        dataIndex: 'actor',
        key: 'actor',
        width: 230,
        render: (actor: AuditLogList['actor'], record: AuditLogList) => {
          const fullName = toDisplayString(actor?.full_name)
          const email = toDisplayString(actor?.email)
          const fallbackName =
            fullName ||
            email ||
            toDisplayString(actor?.id) ||
            getActorlessLabel(record.actor_type, t)

          return fallbackName ? (
            <UserCell
              firstName={fallbackName}
              email={fullName ? email : undefined}
              size="sm"
            />
          ) : (
            <Text type="secondary">—</Text>
          )
        }
      },
      {
        title: t('workspace.apps.auditTab.columns.description'),
        dataIndex: 'description',
        key: 'description',
        ellipsis: { showTitle: false },
        render: (description: string | null) =>
          description ? (
            <Tooltip placement="topLeft" title={description}>
              <Text ellipsis style={{ fontSize: 12 }}>
                {description}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          )
      }
    ],
    [t]
  )

  return (
    <div ref={tableTopRef} style={{ scrollMarginTop: TABLE_SCROLL_MARGIN_TOP }}>
      {isError ? (
        <Alert
          type="error"
          showIcon
          title={t('workspace.apps.auditTab.loadError')}
          action={
            <Button size="small" onClick={() => void refetch()}>
              {t('workspace.apps.auditTab.retry')}
            </Button>
          }
          style={{ marginBottom: 14 }}
        />
      ) : null}

      <ProTableContainer<AuditLogList>
        tableIdentifier="app-audit-events"
        searchPlaceholder={t(
          'workspace.apps.auditTab.filters.searchPlaceholder'
        )}
        rowKey="id"
        loading={{
          spinning: isLoading || isFetching,
          delay: 200
        }}
        data={{ count: total, results: rows }}
        columns={columns}
        onChange={(pagination) => {
          if (
            (pagination.current != null && pagination.current !== page) ||
            (pagination.pageSize != null && pagination.pageSize !== perPage)
          ) {
            scrollToTableTop()
          }
        }}
        onRow={(record) => {
          const openDetails = () =>
            navigate(
              paths.workspaces.apps.auditLogDetails(
                String(organizationId),
                String(applicationId),
                record.id
              ),
              {
                state: {
                  auditListPath: `${location.pathname}${location.search}`
                }
              }
            )

          return {
            'aria-label': t('workspace.apps.auditTab.openDetails', {
              event: translateAuditAction(t, getAuditEventCode(record))
            }),
            role: 'button',
            tabIndex: 0,
            onClick: openDetails,
            onKeyDown: (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                openDetails()
              }
            }
          }
        }}
        scroll={{ x: 770, scrollToFirstRowOnChange: true }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t(
                filtersDirty
                  ? 'workspace.apps.auditTab.noMatches'
                  : 'workspace.apps.auditTab.empty'
              )}
            />
          )
        }}
        cardTitle={header ?? viewSelector}
        cardExtra={
          <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
            {header ? viewSelector : null}
            <Text
              type="secondary"
              style={{ fontSize: 12, whiteSpace: 'nowrap' }}
            >
              {t('workspace.apps.auditTab.totalEvents', { count: total })}
            </Text>
            <Tooltip title={t('workspace.apps.auditTab.refresh')}>
              <Button
                size="small"
                type="text"
                icon={<RotateCw size={14} />}
                onClick={() => void refetch()}
                loading={isFetching}
              />
            </Tooltip>
            {canExportAudit ? (
              <>
                <Divider orientation="vertical" style={{ margin: 0 }} />
                <Button
                  size="small"
                  icon={<Download size={14} />}
                  onClick={() => setExportOpen(true)}
                >
                  {t('workspace.apps.auditTab.export')}
                </Button>
              </>
            ) : null}
          </Flex>
        }
        leftChildren={
          <>
            <FilterSelect
              value={actionFilter}
              options={action.options}
              onSelect={(value) => setFilter({ action: value || null })}
              filterKey="action"
              onSearch={action.onSearch}
              placeholder={t(
                'workspace.apps.auditTab.filters.actionPlaceholder'
              )}
              width={FILTER_CONTROL_WIDTH}
            />
            <FilterSelect
              value={actorIdFilter}
              options={actor.options}
              onSelect={(value) => setFilter({ actor_id: value || null })}
              filterKey="actor_id"
              onSearch={actor.onSearch}
              placeholder={t(
                'workspace.apps.auditTab.filters.actorIdPlaceholder'
              )}
              width={FILTER_CONTROL_WIDTH}
              loading={actor.loading}
            />
            <MoreFilters
              filterActiveCount={moreFiltersActiveCount}
              onResetMoreFilters={resetMoreFilters}
              buttonProps={{ size: 'small' }}
            >
              <MoreFilters.Col
                title={t(
                  'workspace.apps.auditTab.filters.entityTypePlaceholder'
                )}
              >
                <FilterSelect
                  value={entityTypeFilter}
                  options={entityType.options}
                  filterKey="entity_type"
                  onSelect={(value) =>
                    setFilter({ entity_type: value || null })
                  }
                  onSearch={entityType.onSearch}
                  placeholder={t(
                    'workspace.apps.auditTab.filters.entityTypePlaceholder'
                  )}
                  width="100%"
                />
              </MoreFilters.Col>

              <MoreFilters.Col
                title={t('workspace.apps.auditTab.filters.entityIdPlaceholder')}
              >
                <SearchInput
                  queryParam="entity_id"
                  size="small"
                  style={{ width: '100%' }}
                  placeholder={t(
                    'workspace.apps.auditTab.filters.entityIdPlaceholder'
                  )}
                />
              </MoreFilters.Col>

              <MoreFilters.Col
                title={t(
                  'workspace.apps.auditTab.filters.actorTypePlaceholder'
                )}
              >
                <FilterSelect
                  value={actorTypeFilter}
                  options={actorType.options}
                  onSelect={handleActorTypeChange}
                  filterKey="actor_type"
                  onSearch={actorType.onSearch}
                  placeholder={t(
                    'workspace.apps.auditTab.filters.actorTypePlaceholder'
                  )}
                  width="100%"
                />
              </MoreFilters.Col>

              <MoreFilters.Col
                title={t('workspace.apps.auditTab.filters.dateRangeLabel')}
              >
                <RangePicker
                  size="small"
                  style={{ width: '100%' }}
                  value={rangeValue}
                  onChange={(next) =>
                    handleRangeChange(
                      next as [Dayjs | null, Dayjs | null] | null
                    )
                  }
                  aria-label={t(
                    'workspace.apps.auditTab.filters.dateRangeLabel'
                  )}
                />
              </MoreFilters.Col>
            </MoreFilters>

            <Flex flex="1 0 100%">
              <ActiveFilterTags
                items={filterTagItems}
                onClearAll={filtersDirty ? clearFilters : undefined}
              />
            </Flex>
          </>
        }
      />

      <AuditExportModal
        key={exportOpen ? 'open' : 'closed'}
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        organizationId={organizationId}
        applicationId={applicationId}
        defaultPeriod={rangeValue}
        viewExportsPath={paths.workspaces.apps.auditExports(
          String(organizationId),
          String(applicationId)
        )}
        filters={{
          ...(searchTerm ? { search: searchTerm } : {}),
          ...(entityTypeFilter ? { entity_type: entityTypeFilter } : {}),
          ...(entityIdFilter ? { entity_id: entityIdFilter } : {}),
          ...(actionFilter ? { action: actionFilter } : {}),
          ...(actorIdFilter ? { actor_id: actorIdFilter } : {})
        }}
      />
    </div>
  )
}

export const EventsPanel = (props: Props) => (
  <FilterLabelsProvider>
    <EventsPanelBase {...props} />
  </FilterLabelsProvider>
)
